import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type Ref
} from 'vue';

export type CeTheme = 'dark' | 'light';

/** Resolved CSS custom properties per theme — written inline on CE host for iOS Safari. */
export const CE_THEME_TOKENS: Record<CeTheme, Record<string, string>> = {
  dark: {
    '--background': '#000205',
    '--foreground': '#eeeeee',
    '--card': '#01060c',
    '--primary': '#00BBCC',
    '--primary-foreground': '#171717',
    '--muted': '#0e171e',
    '--muted-foreground': '#a1a1a1',
    '--border': '#14242f',
    '--border-subtle': 'rgba(20, 36, 47, 0.75)',
    '--shadow-soft': '0 8px 24px rgba(0, 0, 0, 0.45)',
    '--cdn-glow': '#00c7d0',
    '--mobile-menu-panel-bg':
      'linear-gradient(180deg, rgba(1, 6, 12, 0.98) 0%, rgba(1, 6, 12, 1) 100%)'
  },
  light: {
    '--background': '#f7f9fb',
    '--foreground': '#0c1220',
    '--card': '#ffffff',
    '--primary': '#00BBCC',
    '--primary-foreground': '#ffffff',
    '--muted': '#eef2f6',
    '--muted-foreground': '#5c6b7a',
    '--border': '#d8e0e8',
    '--border-subtle': '#dfe6ed',
    '--shadow-soft': '0 8px 24px rgba(15, 23, 42, 0.08)',
    '--cdn-glow': '#00BBCC',
    '--mobile-menu-panel-bg':
      'linear-gradient(180deg, #ffffff 0%, #f7f9fb 100%)'
  }
};

function readDocumentTheme(): CeTheme {
  if (typeof document === 'undefined') return 'dark';
  const root = document.documentElement;
  const data = root.dataset.theme;
  if (data === 'light' || data === 'dark') return data;
  return root.classList.contains('dark') ? 'dark' : 'light';
}

function resolveHostEl(): (HTMLElement & { theme?: string }) | null {
  const instance = getCurrentInstance();
  const raw = instance?.vnode.el as Node | null | undefined;
  const root = raw && 'getRootNode' in raw ? raw.getRootNode() : null;
  if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
    return root.host as HTMLElement & { theme?: string };
  }
  return null;
}

function applyThemeTokens(host: HTMLElement, theme: CeTheme) {
  const tokens = CE_THEME_TOKENS[theme];
  for (const [name, value] of Object.entries(tokens)) {
    host.style.setProperty(name, value);
  }
}

/**
 * Web Component 主题同步（iOS Safari 关键）。
 *
 * 背景：
 * - `:host-context(html.dark)` 在 iOS 上不可靠
 * - 仅改 host attribute 时，`:host([theme=...])` 有时也不重算
 * - 可靠做法：把解析后的 theme 传给 Shadow 内部节点用 class 驱动样式，
 *   同时双写 host attribute / property，并把 token 内联到 host.style
 *
 * `theme` 为空时跟随 `html.dark` / `data-theme`；显式 `dark|light` 则锁定。
 */
export function useCeHostTheme(themeProp: Ref<string | undefined>) {
  const documentTheme = ref<CeTheme>(readDocumentTheme());
  const autoFollow = ref(themeProp.value !== 'dark' && themeProp.value !== 'light');

  let hostEl: (HTMLElement & { theme?: string }) | null = null;
  let observer: MutationObserver | null = null;
  let syncing = false;

  const resolvedTheme = computed<CeTheme>(() => {
    if (!autoFollow.value) {
      return themeProp.value === 'light' ? 'light' : 'dark';
    }
    return documentTheme.value;
  });

  const applyToHost = (theme: CeTheme) => {
    if (!hostEl) return;
    const current = hostEl.getAttribute('theme');
    if (current !== theme) {
      syncing = true;
      hostEl.setAttribute('theme', theme);
      queueMicrotask(() => {
        syncing = false;
      });
    }
    try {
      hostEl.theme = theme;
    } catch {
      /* ignore non-configurable property */
    }
    hostEl.style.setProperty('--gcsa-ce-theme', theme);
    applyThemeTokens(hostEl, theme);
  };

  const refreshDocumentTheme = () => {
    documentTheme.value = readDocumentTheme();
  };

  watch(
    () => themeProp.value,
    (value) => {
      if (syncing) return;
      if (value === 'dark' || value === 'light') {
        autoFollow.value = false;
      } else {
        autoFollow.value = true;
        refreshDocumentTheme();
      }
    }
  );

  watch(
    resolvedTheme,
    (theme) => {
      applyToHost(theme);
    },
    { flush: 'post' }
  );

  onMounted(() => {
    hostEl = resolveHostEl();
    if (themeProp.value === 'dark' || themeProp.value === 'light') {
      autoFollow.value = false;
    } else {
      autoFollow.value = true;
    }
    refreshDocumentTheme();
    applyToHost(resolvedTheme.value);

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        if (!autoFollow.value) return;
        refreshDocumentTheme();
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme']
      });
    }
  });

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
    hostEl = null;
  });

  return { resolvedTheme };
}
